import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_NAME = 'AyodhyaDham.info';
const SITE_URL = 'https://ayodhyadham.info';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-default.jpg`;
const DEFAULT_DESC =
  'The digital guide to Ayodhya — book darshan at Shri Ram Mandir, explore temples and the ghats of the Saryu, sponsor a puja, find verified stays and plan your pilgrimage.';

/** Language code -> the locale string Open Graph expects. */
const LOCALES = { en: 'en_IN', hi: 'hi_IN', gu: 'gu_IN', mr: 'mr_IN' };

const absolute = (path = '') => (path.startsWith('http') ? path : `${SITE_URL}${path}`);

export default function SEO({
  title,
  description = DEFAULT_DESC,
  canonicalUrl = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  structuredData = null,
  extraSchemas = [],
  keywords = '',
  /** ISO dates for article-type pages. */
  publishedTime = '',
  modifiedTime = '',
  // Legacy prop alias kept for backwards compatibility.
  schema = null,
}) {
  const { i18n } = useTranslation();
  const lang = LOCALES[i18n.language] ? i18n.language : 'en';

  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Ayodhya Pilgrimage Guide`;
  const canonical = canonicalUrl ? absolute(canonicalUrl) : SITE_URL;
  const ogImage = absolute(image);
  const finalStructuredData = structuredData || schema || null;

  // Every page exists in four languages, selected by ?lang=. x-default is English.
  const alternates = Object.keys(LOCALES).map((code) => ({
    code,
    href: code === 'en' ? canonical : `${canonical}${canonical.includes('?') ? '&' : '?'}lang=${code}`,
  }));

  // BreadcrumbList is emitted by the Breadcrumbs component, which every page
  // with a trail already renders (PageHero included).
  const schemas = [finalStructuredData, ...extraSchemas].filter(Boolean);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {/* Let search and answer engines show full snippets and large previews. */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      )}

      {/* Language alternates */}
      {!noindex && alternates.map((alt) => (
        <link key={alt.code} rel="alternate" hrefLang={`${alt.code}-IN`} href={alt.href} />
      ))}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALES[lang]} />
      {Object.entries(LOCALES)
        .filter(([code]) => code !== lang)
        .map(([code, locale]) => (
          <meta key={code} property="og:locale:alternate" content={locale} />
        ))}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {/* Structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
